export interface PatchArgs {
  syncId: string;
  targetItems: Array<{ item_id: number; board_id: number }>;
  // Same shape as columnValuesResync.value — a raw string for most column types, a dropdown's
  // `values` array for dropdown columns.
  newValue: string | Array<{ id: number | string; label: string }> | null;
  newText: string;
  columnType: string;
  columnTitle: string;
  columnHasDiscrepancy: boolean;
}

/**
 * A name resync writes `items[].name`, not a column value. Every surface that shows a name
 * reads one of three fields — `items[].name` (streamed verdicts and row expands),
 * `board_names[].item_name` (cached verdicts) and the top-level `item_name` — so all three
 * are rewritten for the boards the rename landed on. The name column's own flag flips the
 * same way a column's does, and the badge recounts from the flags.
 */
const patchNameAfterResync = (entry: any, args: PatchArgs, targetIds: Set<string>) => {
  const targetBoardIds = new Set(args.targetItems.map((t) => String(t.board_id)));

  const nextItems = (entry.items ?? []).map((item: any) =>
    targetIds.has(String(item.item_id)) ? { ...item, name: args.newText } : item,
  );
  const nextBoardNames = (entry.board_names ?? []).map((board: any) =>
    targetIds.has(String(board.item_id)) || targetBoardIds.has(String(board.board_id))
      ? { ...board, item_name: args.newText }
      : board,
  );
  const nextSharedColumns = (entry.shared_columns ?? []).map((col: any) =>
    col.column_type === args.columnType ? { ...col, discrepancy: args.columnHasDiscrepancy } : col,
  );

  return {
    ...entry,
    item_name: args.newText,
    items: nextItems,
    ...(entry.board_names ? { board_names: nextBoardNames } : {}),
    shared_columns: nextSharedColumns,
    total_discrepancies: nextSharedColumns.filter((col: any) => col.discrepancy === true).length,
  };
};

export function patchDiscrepanciesAfterResync(discrepancies: any[], args: PatchArgs): any[] {
  return discrepancies.map((entry) => {
    if (entry.syncId !== args.syncId) return entry;

    if (args.columnType === "name") {
      return patchNameAfterResync(
        entry,
        args,
        new Set(args.targetItems.map((t) => String(t.item_id))),
      );
    }

    // Compared as strings on BOTH sides. These ids reach here from two different systems —
    // targetItems' from Mongo via dbItem.item_id, entry.items' from Monday's GraphQL `ID` — and
    // nothing guarantees they arrive as the same JS type. findMatchingColumn (sharedColumnsAdapter)
    // already toString()s both operands for the same reason; a raw Set.has() across a
    // string/number boundary silently matches nothing and patches no item at all.
    const targetIds = new Set(args.targetItems.map((t) => String(t.item_id)));

    /*
     * The board this resync did NOT write to is the one whose value every target is being set
     * to, which makes its cell the only correct source for the presentational fields that ride
     * alongside `value` and `text`.
     *
     * A status label's colour is the one that matters: it belongs to the LABEL, so changing the
     * label changes it, but the caller passes only value/text and has no way to know the new
     * colour. Writing the new text over the old colour leaves a bar reading "Done" in the
     * colour of "Stuck" — a more confident lie than simply not updating.
     */
    const winningCell = (entry.items ?? [])
      .filter((item: any) => !targetIds.has(String(item.item_id)))
      .flatMap((item: any) => item.column_values ?? [])
      .find(
        (col: any) => col.type === args.columnType && col.column?.title === args.columnTitle,
      );

    // Cache-seeded discrepancy entries are flags-only: they carry shared_columns +
    // total_discrepancies but NO per-item `items`/`column_values` (the live values are
    // lazy-fetched into ExpandedRow, not stored here). Guard both levels so resyncing
    // such a row can't throw — the resolution signal below (shared_columns) is what
    // actually drives the optimistic UI in that path.
    const nextItems = (entry.items ?? []).map((item: any) => {
      if (!targetIds.has(String(item.item_id))) return item;
      return {
        ...item,
        column_values: (item.column_values ?? []).map((col: any) =>
          col.type === args.columnType && col.column?.title === args.columnTitle
            ? {
                ...col,
                value: args.newValue,
                text: args.newText,
                // Conditionally spread, so an absent colour stays absent rather than becoming
                // an explicit undefined: MondayValueCell distinguishes "this status carries no
                // colour" (fall back to a theme token) from one that does, and flattening the
                // two would paint an invented default over the customer's own choice.
                ...(winningCell?.label_style ? { label_style: winningCell.label_style } : {}),
              }
            : col
        ),
      };
    });

    const nextSharedColumns = (entry.shared_columns ?? []).map((col: any) =>
      col.column_type === args.columnType && col.column_title === args.columnTitle
        ? { ...col, discrepancy: args.columnHasDiscrepancy }
        : col
    );

    const nextTotal = nextSharedColumns.filter((col: any) => col.discrepancy === true).length;

    return {
      ...entry,
      items: nextItems,
      shared_columns: nextSharedColumns,
      total_discrepancies: nextTotal,
    };
  });
}

export function formatResyncError(
  error: any,
  ctx: { columnTitle: string | null }
): string {
  const scope = ctx.columnTitle ? `the "${ctx.columnTitle}" column` : "this column";

  if (!error?.response) {
    return `Couldn't reach the server to resynchronize ${scope}. Check your connection and try again.`;
  }

  const status = error.response.status;
  if (status === 401 || status === 403) {
    return `You don't have permission to resynchronize ${scope}. Please reload the app.`;
  }
  if (status === 429) {
    return `Monday.com is rate-limiting requests. Wait a moment and try again.`;
  }

  const serverMessage = error.response.data?.message;
  if (serverMessage) {
    return `Couldn't resynchronize ${scope}: ${serverMessage}`;
  }

  return `Couldn't resynchronize ${scope}. Please try again.`;
}
